import { Controller } from '@hotwired/stimulus';
import { postGeneratorOpenAI, getPaywallController } from '../lib/generator_api';
import {
  applyDisplayFormula,
  bindFormulaDelimiterListener,
  delimiterPromptInstruction,
  processGeneratedFormula,
  unbindFormulaDelimiterListener,
} from '../lib/generator_formula_delimiter';

const SPARKLINE_SYSTEM_ROLE = `You are a helpful sparkline formula generator. You will help generate a sparkline formula to use in a google sheet.
Syntax is =SPARKLINE(data, [options])
data - The range or array containing the data to plot.

options - [ OPTIONAL ] - A range or array of optional settings and associated values used to customize the chart.

If referencing a range, options should be two cells wide where the first cell is the option and the second cell is the value that option is set to.

The "charttype" option defines the type of chart to plot, which includes:

"line" for a line graph (the default)
"bar" for a stacked bar chart
"column" for a column chart
"winloss" for a special type of column chart that plots 2 possible outcomes: positive and negative (like a coin toss, heads or tails).
For line graphs:

"xmin" sets the minimum value along the horizontal axis.

"xmax" sets the maximum value along the horizontal axis.

"ymin" sets the minimum value along the vertical axis.

"ymax" sets the maximum value along the vertical axis.

"color" sets the color of the line.
"empty" sets how to treat empty cells. Possible corresponding values include: "zero" or "ignore".
"nan" sets how to treat cells with non-numeric data. Options are: "convert" and "ignore".
"rtl" determines whether or not the chart is rendered right to left. Options are true or false.
"linewidth" determines how thick the line will be in the chart. A higher number means a thicker line.
For column and winloss sparklines:

"color" sets the color of chart columns.
"lowcolor" sets the color for the lowest value in the chart
"highcolor" sets the color for the higest value in the chart
"firstcolor" sets the color of the first column
"lastcolor" sets the color of the last column
"negcolor" sets the color of all negative columns
"empty" sets how to treat empty cells. Possible corresponding values include: "zero" or "ignore".
"nan" sets how to treat cells with non-numeric data. Options are: "convert" and "ignore".
"axis" decides if an axis needs to be drawn (true/false)
"axiscolor" sets the color of the axis (if applicable)
"ymin" sets the custom minimum data value that should be used for scaling the height of columns (not applicable for win/loss)
"ymax" sets the custom maximum data value that should be used for scaling the height of columns (not applicable for win/loss)
"rtl" determines whether or not the chart is rendered right to left. Options are true or false.
For bar charts:

"max" sets the maximum value along the horizontal axis.
"color1" sets the first color used for bars in the chart.
"color2" sets the second color used for bars in the chart.
"empty" sets how to treat empty cells. Possible corresponding values include: "zero" or "ignore".
"nan" sets how to treat cells with non-numeric data. Options are: "convert" and "ignore".
"rtl" determines whether or not the chart is rendered right to left. Options are true or false.
Notes
Colors can be written using their names (e.g., "green") or as a hex code (e.g., "#3D3D3D").`;

const SPARKLINE_PROMPT_PREFIX =
  'You will generate a sparkline formula to use in a google sheet. Only include the sparkline formula. Do not include the explanation in your response. Reply with only the sparkline formula including =SPARKLINE() and then insert the formula and range appropriately. ' +
  String.raw`
Here are some examples:
Use Case: I need to create a red column chart.
Range to use: A1:A5
Response: =SPARKLINE(A1:A5, {"charttype","column"; "axis", true; "axiscolor", "red"})
Use case: I want to make a bar chart with maximum 40
Range to use: A2:E2
Response: =SPARKLINE(A2:E2,{"charttype","bar";"max",40})
Use Case: `;

export default class extends Controller {
  static targets = [
    'usecase',
    'cell',
    'submit',
    'submitLabel',
    'placeholder',
    'loading',
    'output',
    'formulaText',
    'error',
    'successNote',
    'copyButton'
  ];

  static values = {
    tool: { type: String, default: 'sparkliner' }
  };

  connect() {
    bindFormulaDelimiterListener(this);
    this.formulaValue = '';
  }

  disconnect() {
    unbindFormulaDelimiterListener(this);
  }

  paywall() {
    return getPaywallController(this.element);
  }

  parseExample(event) {
    const raw = event.currentTarget.dataset.example;
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch {
      console.error('Invalid example payload');
      return null;
    }
  }

  loadExample(event) {
    const data = this.parseExample(event);
    if (!data) return;

    this.usecaseTarget.value = data.usecase;
    this.cellTarget.value = data.range;
    this.clearError();
    this.hideResult();
    this.highlightExampleCard(event.currentTarget);
    this.scrollToGenerator();
    this.usecaseTarget.focus();
  }

  highlightExampleCard(button) {
    this.element.querySelectorAll('[data-example-card]').forEach((card) => {
      card.classList.remove('ring-2', 'ring-emerald-500/60', 'ring-offset-2');
    });
    button.closest('[data-example-card]')?.classList.add('ring-2', 'ring-emerald-500/60', 'ring-offset-2');
  }

  scrollToGenerator() {
    document.getElementById('sparkliner-generator')?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  }

  clearError() {
    if (this.hasErrorTarget) {
      this.errorTarget.classList.add('hidden');
      this.errorTarget.textContent = '';
    }
  }

  async submit(event) {
    event.preventDefault();
    this.clearError();

    const usecase = this.usecaseTarget.value.trim();
    if (!usecase) {
      this.showError('Describe the chart you want before generating.');
      return;
    }

    const cellRaw = this.cellTarget.value;
    const cellValue = !cellRaw || cellRaw.trim() === '' ? 'A1' : cellRaw.trim();

    const prompt =
      SPARKLINE_PROMPT_PREFIX +
      delimiterPromptInstruction() +
      usecase +
      `
Range to use: ` +
      cellValue;

    this.setLoading(true);
    this.hideResult();

    try {
      const result = await postGeneratorOpenAI({
        userRole: SPARKLINE_SYSTEM_ROLE,
        prompt,
        generator: this.toolValue
      });

      if (result.limited) {
        this.paywall()?.handleLimitReached();
        return;
      }

      if (result.error) {
        console.error('SPARKLINE generator proxy error', result.response?.status, result.detail);
        this.showError('Could not generate a formula. Try again in a moment.');
        return;
      }

      const data = result.data;
      const rawFormula = data.choices?.[0]?.message?.content?.trim();

      if (!rawFormula) {
        console.error('OpenAI proxy returned no SPARKLINE formula', data);
        this.showError('No formula came back. Add more detail and try again.');
        return;
      }

      const { canonical, display } = processGeneratedFormula(rawFormula);
      this.canonicalFormula = canonical;
      this.formulaValue = display;
      await this.copyToClipboard(display);
      this.paywall()?.applyUsageFromResponse(data.generator_usage);
      this.showResult(display);

      if (data.generator_usage?.remaining === 1) {
        this.showSuccessNudge();
      }

      if (data.generator_usage?.remaining === 0) {
        this.paywall()?.handleLimitReached();
      }
    } catch (err) {
      console.error('Sparkliner request failed', err);
      this.showError('Connection failed. Check your network and try again.');
    } finally {
      this.setLoading(false);
    }
  }

  async copy(event) {
    event?.preventDefault();
    if (!this.formulaValue) return;

    await this.copyToClipboard(this.formulaValue);
    if (this.hasSuccessNoteTarget) {
      this.successNoteTarget.textContent = 'Copied again.';
      this.successNoteTarget.classList.remove('hidden');
    }
  }

  async copyToClipboard(text) {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // Clipboard may be blocked; formula remains visible in the output panel.
    }
  }

  setLoading(isLoading) {
    if (this.hasSubmitTarget) {
      const paywall = this.paywall();
      const atLimit = paywall && !paywall.paidMemberValue && paywall.remainingValue === 0;
      if (atLimit) {
        paywall.syncUi(0);
      } else {
        this.submitTarget.disabled = isLoading;
      }
    }
    if (this.hasSubmitLabelTarget) {
      this.submitLabelTarget.textContent = isLoading ? 'Generating…' : 'Generate';
    }
    if (this.hasLoadingTarget) this.loadingTarget.classList.toggle('hidden', !isLoading);
    if (this.hasPlaceholderTarget) this.placeholderTarget.classList.toggle('hidden', isLoading);
    if (isLoading && this.hasOutputTarget) this.outputTarget.classList.add('hidden');
    if (isLoading && this.hasCopyButtonTarget) this.copyButtonTarget.classList.add('hidden');
    if (isLoading && this.hasSuccessNoteTarget) this.successNoteTarget.classList.add('hidden');
  }

  hideResult() {
    if (this.hasOutputTarget) this.outputTarget.classList.add('hidden');
    if (this.hasPlaceholderTarget) this.placeholderTarget.classList.remove('hidden');
    if (this.hasCopyButtonTarget) this.copyButtonTarget.classList.add('hidden');
    if (this.hasSuccessNoteTarget) this.successNoteTarget.classList.add('hidden');
  }

  showResult(formula) {
    if (this.hasPlaceholderTarget) this.placeholderTarget.classList.add('hidden');
    applyDisplayFormula(this, formula);
    if (this.hasOutputTarget) this.outputTarget.classList.remove('hidden');
    if (this.hasCopyButtonTarget) this.copyButtonTarget.classList.remove('hidden');
    if (this.hasSuccessNoteTarget) {
      this.successNoteTarget.textContent = 'Copied to clipboard. Paste into your cell.';
      this.successNoteTarget.classList.remove('hidden');
    }
  }

  showSuccessNudge() {
    if (this.hasSuccessNoteTarget) {
      this.successNoteTarget.textContent = 'Nice. 1 free generation left on this tool.';
      this.successNoteTarget.classList.remove('hidden');
    }
  }

  showError(message) {
    if (this.hasErrorTarget) {
      this.errorTarget.textContent = message;
      this.errorTarget.classList.remove('hidden');
    }
    this.hideResult();
  }
}
